import idx from "idx";
import React from "react";
import { FaTrash } from "react-icons/fa";
import { SortableContainer, SortableElement, SortableElementProps, SortableHandle } from "react-sortable-hoc";

import { PreferredLocale } from "../../../../../../utils/locales";
import ActionConfirmationButton from "../../../../../pages/CourseAuthor/ActionConfirmation";
import { ILocalizedListItem, ILocalizedListItemAnswer } from "../../../../types";
import { AnswerItem } from "../answer-item/AnswerItem";
import { Label, LabelsContainer, RemoveIcon } from "../common.styles";
import {
  DragBars,
  HiddenInput,
  ListItemNumber,
  ListItemOptions,
  ListItemsContainer,
} from "./ListItemsExercisePart.styles";
import { IListItemsExercisePartProps } from "./types";

interface ISortListItemProps extends IListItemsExercisePartProps {
  listItem: ILocalizedListItem;
}

const SortBars = SortableHandle(() => <DragBars />);

export const SortListItem = SortableElement((props: ISortListItemProps & SortableElementProps) => {
  const { changeListItemAnswers, currentLocale, exercisePart, index, isEditing, listItem, partOperations } = props;
  const localizedAnswers = listItem.answers.elems.find(a => a.locale === currentLocale);
  const answers = idx(localizedAnswers, l => l.value.value) || [];
  const hint = listItem.hint.elems.find(h => h.locale === currentLocale);

  const onReturnHit = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && event.which === 13) {
      event.currentTarget.blur();
      event.currentTarget.focus();
    }
  };

  const pushAnswer = (listItemId: number, listItemAnswers?: ILocalizedListItemAnswer) => (
    event: React.FocusEvent<HTMLInputElement>
  ): void => {
    const answer = event.currentTarget.value.trim();
    if (answer !== "") {
      const oldAnswers = (listItemAnswers && listItemAnswers.value.value) || [];
      const newAnswers = [...oldAnswers, answer];

      changeListItemAnswers({
        exercise: exercisePart,
        listItemId,
        locale: currentLocale,
        newAnswers,
      });
      event.currentTarget.value = "";
    }
  };

  const removeAnswer = (listItemId: number, listItemAnswers: ILocalizedListItemAnswer, at: number) => (): void => {
    const oldAnswers = listItemAnswers.value.value;
    const newAnswers = oldAnswers.filter((_, i) => i !== at);

    changeListItemAnswers({
      exercise: exercisePart,
      listItemId,
      locale: currentLocale,
      newAnswers,
    });
  };

  const removeListItem = (listItemId: number) => () => {
    partOperations.deleteExerciseItem(listItemId)();
  };

  const storeHint = (listItemId: number) => (content: string, locale?: PreferredLocale) => {
    partOperations.modifyExerciseItemHint(listItemId)(content, locale);
  };

  return (
    <ListItemsContainer>
      <ListItemNumber>{`${index + 1}.`}</ListItemNumber>
      <AnswerItem
        localizedHint={hint}
        currentLocale={currentLocale}
        modifyExerciseItemHint={storeHint(listItem.id)}
        isEditing={isEditing}
      >
        <LabelsContainer>
          {localizedAnswers &&
            answers.map((answer, i) => (
              <Label isEditing={isEditing} key={i}>
                {answer}
                {isEditing && <RemoveIcon onClick={removeAnswer(listItem.id, localizedAnswers, i)} />}
              </Label>
            ))}
          {isEditing && (
            <>
              <HiddenInput onBlur={pushAnswer(listItem.id, localizedAnswers)} onKeyPress={onReturnHit} />
              <ListItemOptions>
                <SortBars />
                <ActionConfirmationButton ActionIcon={FaTrash} action="Delete" actionFn={removeListItem(listItem.id)} />
              </ListItemOptions>
            </>
          )}
        </LabelsContainer>
      </AnswerItem>
    </ListItemsContainer>
  );
});

export const SortableListItems = SortableContainer((props: IListItemsExercisePartProps) => {
  const { currentLocale, exercisePart } = props;
  const listItems = exercisePart.content.items.elems
    .map<ILocalizedListItem>((listItem, index) => ({
      ...listItem,
      sort: index,
    }))
    .filter(listItem => !listItem.deleted);

  return (
    <div>
      {listItems.map(listItem => (
        <SortListItem
          {...props}
          index={listItem.sort}
          key={`${exercisePart.id}-${currentLocale}-${listItem.id}`}
          listItem={listItem}
        />
      ))}
    </div>
  );
});
